import { useCurrentUser, useToastProvider } from "@qualio/ui-components";
import { useCurrentProduct } from "./useCurrentProduct";
import { useMutation, useQueryClient } from "react-query";
import { designControlsApi } from "../api/designControls";
import { TestResult } from "@design-controls/types";
import { useTranslation } from "react-i18next";

export const useAddTestResult = () => {
  const { t } = useTranslation();
  const { companyId } = useCurrentUser();
  const { id: productId } = useCurrentProduct();
  const { showToast } = useToastProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ testCase, ...result }: TestResult & { testCase: string }) =>
      designControlsApi.addTestResult(companyId, productId, result, testCase),
    onSuccess: async (_, { testCase }) => {
      showToast({
        title: t("testCases.notifications.addResultSuccess.title"),
        description: t("testCases.notifications.addResultSuccess.description"),
        status: "success",
        duration: 5000,
        "data-cy": "toast-test-result-add-success",
      });
      await Promise.all([
        queryClient.invalidateQueries([
          "DESIGN_ELEMENT_BY_ID",
          companyId,
          productId,
          testCase,
          "v0",
        ]),
        queryClient.invalidateQueries([
          "DESIGN_ELEMENT_VERSIONS",
          companyId,
          productId,
          testCase,
        ]),
        queryClient.invalidateQueries([
          "OPEN_ISSUE_LIST",
          companyId,
          productId,
        ]),
      ]);
    },
    onError: () => {
      showToast({
        title: t("testCases.notifications.addResultError.title"),
        description: t("testCases.notifications.addResultError.description"),
        status: "error",
        duration: 5000,
        "data-cy": "toast-test-result-add-error",
      });
    },
  });
};
